import type { AdminUserDetails, LinkedIdentity } from "@/common/model/AdminUserDetails";
import { formatRelativeTime } from "@/utils/authActivityUtils";

interface ConnectionDetailsTabProps {
    userDetails?: AdminUserDetails;
}

const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        />
        <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
        />
        <path
            fill="#FBBC05"
            d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"
        />
        <path
            fill="#EA4335"
            d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06L5.84 9.9c.87-2.6 3.3-4.53 6.16-4.53z"
        />
    </svg>
);

const AppleIcon = () => (
    <svg className="w-5 h-5 text-gray-900" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
);

const providerMeta = (provider: string): { name: string; icon: React.ReactNode } => {
    switch (provider) {
        case "google":
            return { name: "Google", icon: <GoogleIcon /> };
        case "apple":
            return { name: "Apple", icon: <AppleIcon /> };
        default:
            return { name: provider, icon: null };
    }
};

export const ConnectionDetailsTab: React.FC<ConnectionDetailsTabProps> = ({ userDetails }) => {
    if (!userDetails) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-sm text-gray-500 italic">No connection information available</p>
            </div>
        );
    }

    const formatDate = (timestamp?: number) => {
        if (!timestamp) return null;
        return new Date(timestamp).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const ConnectionItem = ({
        name,
        connected,
        connectedDate,
        icon,
    }: {
        name: string;
        connected: boolean;
        connectedDate?: number;
        icon: React.ReactNode;
    }) => (
        <div className="flex items-center justify-between py-3 px-4 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">{icon}</div>
                <div>
                    <p className="text-sm font-medium text-gray-900">{name}</p>
                    {connected && connectedDate && (
                        <p className="text-xs text-gray-500">Connected on {formatDate(connectedDate)}</p>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2">
                {connected ? (
                    <>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Connected
                        </span>
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </>
                ) : (
                    <>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Not Connected
                        </span>
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </>
                )}
            </div>
        </div>
    );

    const identities: LinkedIdentity[] = userDetails.linkedIdentities ?? [];
    const identitiesUnavailable = userDetails.linkedIdentitiesUnavailable === true;
    // OAuth-only accounts with no provider email get a synthetic, non-routable username —
    // email sign-in does NOT work for them, so don't advertise it as available.
    const isSyntheticEmail = (userDetails.username ?? "")
        .toLowerCase()
        .endsWith("@oauth.placeholder.thesurfr.app");

    return (
        <div className="space-y-6">
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-900">Sign-in Methods</h3>
                    {userDetails.lastActiveAt && (
                        <span className="text-xs text-gray-500">
                            Last active {formatRelativeTime(userDetails.lastActiveAt)}
                        </span>
                    )}
                </div>
                <div className="space-y-2">
                    {/* Email sign-in — the account's username is the email. Marked "Not available"
                        for OAuth-only accounts whose username is a synthetic placeholder (no real email). */}
                    <div className="flex items-center justify-between py-3 px-4 bg-white rounded-lg border border-gray-200">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-900">Email</p>
                                <p className="text-xs text-gray-500">
                                    {isSyntheticEmail ? "No email on file (OAuth placeholder)" : userDetails.username}
                                </p>
                            </div>
                        </div>
                        {isSyntheticEmail ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                Not available
                            </span>
                        ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Available
                            </span>
                        )}
                    </div>

                    {identities.map((identity) => {
                        const { name, icon } = providerMeta(identity.provider);
                        return (
                            <div
                                key={identity.provider}
                                className="flex items-center justify-between py-3 px-4 bg-white rounded-lg border border-gray-200"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                                        {icon}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{name}</p>
                                        <p className="text-xs text-gray-500">
                                            {identity.createdAt && `Linked ${formatDate(identity.createdAt)}`}
                                            {identity.lastLoginAt && ` · last used ${formatRelativeTime(identity.lastLoginAt)}`}
                                        </p>
                                        {identity.email && (
                                            <p className="text-xs text-gray-400">
                                                {identity.email}
                                                {identity.emailVerified ? " (verified)" : ""}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Linked
                                </span>
                            </div>
                        );
                    })}

                    {identities.length === 0 && identitiesUnavailable && (
                        <p className="text-xs text-amber-600 italic px-1">
                            Could not load linked sign-in providers (data read failed). Try refreshing.
                        </p>
                    )}
                    {identities.length === 0 && !identitiesUnavailable && (
                        <p className="text-xs text-gray-500 italic px-1">
                            No social sign-in providers linked (email sign-in only).
                        </p>
                    )}
                </div>
            </div>

            <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Connected Services</h3>
                <div className="space-y-2">
                    <ConnectionItem
                        name="Strava"
                        connected={userDetails.hasStravaConnected ?? false}
                        connectedDate={userDetails.stravaConnectedDate}
                        icon={
                            <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
                            </svg>
                        }
                    />
                    <ConnectionItem
                        name="Garmin"
                        connected={userDetails.hasGarminConnected ?? false}
                        icon={
                            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12.954 11.616L8.507 7.17l4.447-4.447a.524.524 0 0 0 0-.74L11.717.744a.524.524 0 0 0-.74 0L6.53 5.19l-4.447-4.447a.524.524 0 0 0-.74 0L.104 2.983a.524.524 0 0 0 0 .74l4.447 4.447-4.447 4.447a.524.524 0 0 0 0 .74l1.24 1.24c.203.203.535.203.74 0l4.447-4.447 4.447 4.447-4.447 4.447a.524.524 0 0 0 0 .74l1.24 1.24c.203.203.535.203.74 0l4.447-4.447 4.447 4.447c.203.203.535.203.74 0l1.24-1.24a.524.524 0 0 0 0-.74l-4.447-4.447 4.447-4.447c.203-.203.203-.535 0-.74l-1.24-1.24a.524.524 0 0 0-.74 0l-4.447 4.447z" />
                            </svg>
                        }
                    />
                    <ConnectionItem
                        name="The Wind Games (TWG)"
                        connected={userDetails.hasTWGConnected ?? false}
                        icon={
                            <svg
                                className="w-5 h-5 text-cyan-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5"
                                />
                            </svg>
                        }
                    />
                    <ConnectionItem
                        name="Smart Watch"
                        connected={userDetails.hasWatch ?? false}
                        icon={
                            <svg
                                className="w-5 h-5 text-gray-700"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        }
                    />
                </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 italic">
                    Note: Smart Watch includes both Apple Watch and WearOS devices. The system does not distinguish
                    between the two platforms.
                </p>
            </div>
        </div>
    );
};
